/**
 * The host side of the support messenger's Ask tab (OWA-1872), which replaced the AI
 * search window. The messenger page holds no credentials, so it posts each question
 * here; this module asks AI search with this tab's own sign-in through aiSearchApi,
 * and posts back the answer as HTML already sanitized by renderAnswer. Questions and
 * answers never reach Chatwoot.
 *
 * Every reply is bound to the account that asked: a lock, sign-out or account switch
 * aborts the question in flight, clears the page's conversation and checks again
 * whether AI search serves the new account.
 */
import i18n from '../i18n/i18n';
import type {AiSearchOutcome, AiSearchStatus} from '../AiSearch/aiSearchApi';

const t = (key: string, params?: any) => i18n.ls('aiSearch', key, params);

const FAILURE_KEYS: Record<Exclude<AiSearchOutcome, 'answered'>, string> = {
    refused: 'refused',
    truncated: 'truncated',
    tool_limit: 'toolLimit',
    rejected: 'rejected',
    protected_data: 'protectedData',
    model_error: 'unavailable',
    tools_unavailable: 'unavailable',
};

// AI search question IDs, which are UUIDs. Anything else from the page is ignored.
const QUESTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_QUESTION_CHARS = 10_000;

type Post = (message: object) => void;

type Api = typeof import('../AiSearch/aiSearchApi');

// Loaded when first needed, not with the messenger: aiSearchApi reaches the API client and
// currentLogin, which many test files mock with only a few getters and which load the
// messenger through ErrorBoundaryO. A failed load means Ask is unavailable and is retried.
let apiLoad: Promise<Api | null> | null = null;
const loadApi = () => apiLoad ??= import('../AiSearch/aiSearchApi').catch(() => {
    apiLoad = null;
    return null;
});

export type AskBridge = {
    /** Handles a page message meant for Ask; false for any other message. */
    message: (data: any) => boolean;
    /** The page reported ready: tell it whether Ask is available. */
    ready: () => void;
    /** The tab's account changed (sign-in, lock, switch or logout). */
    accountChanged: () => void;
};

/** `post` sends to the messenger page; `account` is this tab's `firmId:userId`, or '' when not signed in. */
export function createAskBridge(post: Post, account: () => string): AskBridge {
    let status: AiSearchStatus | null = null;
    // The account the status and conversation belong to, and the check in flight for it.
    let statusFor = '';
    let checking: Promise<void> | null = null;
    let pageReady = false;
    let inFlight: {id: number; account: string; controller: AbortController} | null = null;
    let renderer: Promise<typeof import('../AiSearch/renderAnswer')> | null = null;

    const sendStatus = () => {
        if (!pageReady) return;
        post(status
            ? {type: 'outlaw-ai-status', available: true, maxQuestionChars: status.maxQuestionChars,
                notice: status.dataSource === 'fixtures' ? t('fixtureNotice') : ''}
            : {type: 'outlaw-ai-status', available: false});
    };

    const cancel = () => {
        inFlight?.controller.abort();
        inFlight = null;
    };

    /** Checks whether AI search serves the current account, once per account. */
    const check = () => {
        const current = account();
        if (current === statusFor && (checking || status || !current)) return;
        statusFor = current;
        status = null;
        if (!current) { checking = null; return; }
        const run: Promise<void> = loadApi().then(api => api?.getAiSearchStatus() ?? null).catch(() => null).then(found => {
            if (checking !== run || account() !== current) return;
            status = found;
            sendStatus();
        }).finally(() => { if (checking === run) checking = null; });
        checking = run;
    };

    const reply = (id: number, body: object) => post({type: 'outlaw-ai-reply', id, ...body});

    async function ask(id: number, question: string, follows: string | null) {
        const asker = account();
        if (!status || !asker || asker !== statusFor) { reply(id, {kind: 'message', text: t('unavailable')}); return; }
        cancel();
        const controller = new AbortController();
        inFlight = {id, account: asker, controller};
        const current = () => inFlight?.controller === controller && account() === asker;
        try {
            const api = await loadApi();
            if (!current()) return;
            if (!api) { reply(id, {kind: 'message', text: t('unavailable')}); return; }
            const result = await api.askAiSearch(question, follows, controller.signal);
            if (!current()) return;
            if (result.kind === 'answer' && result.answer.outcome === 'answered' && result.answer.answer) {
                // Loaded on the first answer, so the Markdown parser stays out of the main bundle.
                // A failed load (a deploy replaced the chunk, a dropped connection) is retried next time.
                renderer ??= import('../AiSearch/renderAnswer').catch(error => { renderer = null; throw error; });
                const {renderAnswer} = await renderer;
                if (!current()) return;
                reply(id, {kind: 'answer', html: renderAnswer(result.answer.answer), questionId: result.answer.id});
            }
            else if (result.kind === 'answer') {
                const failure = FAILURE_KEYS[result.answer.outcome as Exclude<AiSearchOutcome, 'answered'>] ?? 'unavailable';
                reply(id, {kind: 'message', text: t(failure)});
            }
            else if (result.kind === 'thread_expired') reply(id, {kind: 'expired', text: t('threadExpired')});
            else if (result.kind === 'not_in_pilot') reply(id, {kind: 'message', text: t('notInPilot')});
            else if (result.kind === 'rate_limited') reply(id, {kind: 'message', text: t('rateLimited')});
            else reply(id, {kind: 'message', text: t('unavailable')});
        }
        catch {
            // Aborted by a cancel or account change, or the renderer chunk could not load.
            if (current()) reply(id, {kind: 'message', text: t('unavailable')});
        }
        finally {
            if (inFlight?.controller === controller) inFlight = null;
        }
    }

    async function feedback(questionId: string, helpful: boolean) {
        const asker = account();
        const saved = (await (await loadApi())?.sendAiSearchFeedback(questionId, helpful)) === true;
        if (account() === asker) post({type: 'outlaw-ai-feedback-result', questionId, saved});
    }

    return {
        message(data) {
            if (data?.type === 'outlaw-ai-ask') {
                const {id, question, follows} = data;
                // Without a usable id there is nothing to answer; otherwise the page always gets a
                // reply, so its composer never waits on a question that was never asked.
                if (!Number.isSafeInteger(id)) return true;
                if (typeof question !== 'string' || !question.trim()) reply(id, {kind: 'message', text: t('unavailable')});
                else if (question.length > MAX_QUESTION_CHARS) reply(id, {kind: 'message', text: t('rejected')});
                else if (follows !== null && follows !== undefined && !(typeof follows === 'string' && QUESTION_ID.test(follows))) reply(id, {kind: 'message', text: t('unavailable')});
                else void ask(id, question.trim(), follows ?? null);
                return true;
            }
            if (data?.type === 'outlaw-ai-cancel') { cancel(); return true; }
            if (data?.type === 'outlaw-ai-feedback') {
                if (typeof data.questionId === 'string' && QUESTION_ID.test(data.questionId) && typeof data.helpful === 'boolean') {
                    void feedback(data.questionId, data.helpful);
                }
                return true;
            }
            return false;
        },
        ready() {
            pageReady = true;
            check();
            // A check in flight posts its own result.
            if (!checking) sendStatus();
        },
        accountChanged() {
            if (account() === statusFor) return;
            cancel();
            if (pageReady) post({type: 'outlaw-ai-reset'});
            check();
            // Ask hides at once; the new account's check shows it again if AI search serves it.
            sendStatus();
        },
    };
}
